import { FormStateContext } from "@/components/bli-medlem/form-state-context";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { PageContent } from "@/components/layout/page-content";
import { DynamicZone } from "@/components/ui/dynamic-zone";
import { SelectCenterButton } from "@/components/ui/select-center-button";
import { BliMedlemCentersDocument, type BliMedlemCentersQuery } from "@/generated/client.generated";
import { useCentersSortedByDistance } from "@/hooks/use-centers-sorted-by-distance";
import { UsePage } from "@/hooks/use-page";
import { paths } from "@/utils/paths-utils";
import { useSuspenseQuery } from "@apollo/client";
import { usePathname, useSearchParams } from "next/navigation";
import { type SyntheticEvent, useContext } from "react";
import { LocationButton } from "../center/location-button";
import { VerticalContainer } from "../layout/vertical-container";
import { ErrorMessageString } from "../ui/error-message";
import { sendStepEvent } from "./analytics";
import { sortCenters } from "./utils";

export function MainCenterForm() {
  const page = UsePage("/bli-medlem");
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const { formData, currentStepIndex, step, next } = useContext(FormStateContext);

  const { data } = useSuspenseQuery<BliMedlemCentersQuery>(BliMedlemCentersDocument);

  const sortedCenters = sortCenters([...data.Centers]);
  const {
    askedForLocationLoading,
    centers: askedForLocationCenters,
    setAskedForLocation,
    errorMessage,
  } = useCentersSortedByDistance({});

  const centers = askedForLocationCenters && !askedForLocationLoading ? askedForLocationCenters : sortedCenters;

  return (
    <PageContent width="large">
      <LocationButton
        loading={askedForLocationLoading && !errorMessage}
        onClick={(e: SyntheticEvent) => {
          e.preventDefault();
          setAskedForLocation(true);
        }}
      />
      <HorizontalSpacer size="small" />
      {errorMessage && <ErrorMessageString errorString={errorMessage} />}
      <HorizontalSpacer />

      {step?.showDynamicZone && (
        <VerticalContainer horizontalPlacement="center" width="full">
          <HorizontalSpacer size="medium" />

          <DynamicZone blocks={page?.blocks ?? []} />
        </VerticalContainer>
      )}

      <VerticalContainer horizontalPlacement="center" width="full">
        <HorizontalSpacer size="medium" />

        {centers.map((c) => (
          <SelectCenterButton
            address={
              c.contactInfo?.address
                ? `${c.contactInfo?.address}, ${c.contactInfo?.zipCode} ${c.contactInfo?.city}`
                : undefined
            }
            buttonText="Velg"
            facilities={c.facilities}
            key={c.id}
            href={paths.bliMedlemPrefilled(formData.userJourney, formData.customerType?.id, c.id)}
            onClick={() => {
              //analytics
              sendStepEvent(c.page.title, currentStepIndex + 1, step?.shortTitle, formData.userJourney);

              if (
                `${pathName}?${searchParams.toString()}` ===
                paths.bliMedlemPrefilled(
                  formData.userJourney,
                  formData.customerType?.id,
                  c.id,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                )
              ) {
                next();
              }
            }}
            selected={c.id === formData.mainCenter?.id}
            title={c.page.title}
            imageProps={{
              alt: c.page.thumbnailImage?.alternativeText ?? "Bilde av 3T-senter",
              src: c.page.thumbnailImage?.url ?? "/images/fallback-desktop.jpg",
              width: c.page.thumbnailImage?.width ?? 2000,
              height: c.page.thumbnailImage?.height ?? 800,
              placeholder: c.page.thumbnailImage?.placeholder ?? undefined,
              priority: false,
            }}
          />
        ))}
      </VerticalContainer>
    </PageContent>
  );
}
